/**
 * THE CONTEXT BAR — the one place context is chosen. 09 §3.
 *
 * Binding layout rules implemented here (09 §3.2):
 *   • Sticky at the top of the viewport, full width, one line on desktop.
 *   • Right-anchored. The breadcrumb BEGINS at the right edge with המשרד and proceeds leftwards:
 *       המשרד ‹ החברה ‹ המודול ‹ הישות ‹ התקופה
 *   • The separator is `‹` — never `>` and never `/`. In RTL flow it points forward, i.e. leftwards.
 *   • The period selector and back-to-parent sit together and never move between screens.
 *   • Global utilities sit at the LEFT end — the RTL equivalent of "far corner".
 *   • A slot that does not apply is ABSENT, never `—`, never a greyed dropdown (09 §3.3).
 *
 * This is the only component in the app that calls `useContextSelection`, and therefore the only
 * component that can change the company or the period. `contextSingleSource.test.ts` enforces it.
 */

import { useCallback, useState } from 'react';

import { useContextSelection, useWorkContext } from './ContextProvider';
import { callOperation } from '../api/operations';
import { SurfaceBadge } from './SurfaceBadge';
import { LifecycleChip } from '../components/LifecycleChip';

/** 09 §3.2: the separator, stated once. */
const CHEVRON = '‹';

export interface ContextBarProps {
  /**
   * 09 §1.4: present at every level ≥2 and it goes UP ONE LEVEL — never to the module home and
   * never to browser-history-back semantics. Absent at levels 0 and 1.
   */
  readonly backToParent?: { readonly labelHe: string; readonly href: string };
}

export function ContextBar({ backToParent }: ContextBarProps): JSX.Element {
  const ctx = useWorkContext();
  const selection = useContextSelection('ContextBar');
  const [signingOut, setSigningOut] = useState(false);

  /**
   * End THIS session.
   *
   * The server clears the cookie and revokes the row; the browser then reloads to `/` rather than
   * routing, because the whole point is to discard every piece of in-memory state a signed-in shell
   * was holding. A client-side navigation would keep the old `whoami` answer in React state and show
   * a signed-in shell to somebody who has just signed out.
   *
   * A failure leaves the button enabled and does NOT pretend: a sign-out that reported success while
   * the session lived on is the worst possible lie for this particular control.
   */
  const signOut = useCallback(async (): Promise<void> => {
    setSigningOut(true);
    try {
      await callOperation('auth.session.delete', { body: {} });
      window.location.assign('/');
    } catch {
      setSigningOut(false);
    }
  }, []);

  // 09 §3.1: the משרד slot is shown only when the user belongs to more than one tenant.
  const showTenantSlot = (ctx.session?.tenants.length ?? 0) > 1;

  return (
    <header className="context-bar" data-surface={ctx.surface ?? 'UNREAD'}>
      {/* Right-anchored breadcrumb: reading starts at the right edge. */}
      <nav className="context-bar__chain" aria-label="מיקום במערכת">
        <SurfaceBadge surface={ctx.surface} supportGrantUid={ctx.session?.supportGrantUid ?? null} />

        {showTenantSlot && ctx.tenant !== null ? (
          <>
            <span className="context-bar__sep" aria-hidden="true">
              {CHEVRON}
            </span>
            <label className="context-bar__slot">
              <span className="context-bar__slot-label">המשרד</span>
              <select
                className="context-bar__control"
                value={ctx.tenant.tenantUid}
                onChange={(e) => selection.selectTenant(e.currentTarget.value)}
              >
                {ctx.session?.tenants.map((t) => (
                  <option key={t.tenantUid} value={t.tenantUid}>
                    {t.displayName}
                  </option>
                ))}
              </select>
            </label>
          </>
        ) : null}

        {/*
          THE company picker. There is exactly one instance of this control in the application.
          09 §5.1: the list is the server's single "visible companies" answer, and every row shows an
          explicit lifecycle state — a company's state is displayed, never inferred.
          Absent (not greyed) on a platform screen with no company (09 §3.3).
        */}
        {ctx.surface === 'TENANT' ? (
          <>
            <span className="context-bar__sep" aria-hidden="true">
              {CHEVRON}
            </span>
            <label className="context-bar__slot">
              <span className="context-bar__slot-label">החברה</span>
              <select
                className="context-bar__control context-bar__control--company"
                value={ctx.company?.companyUid ?? ''}
                onChange={(e) => selection.selectCompany(e.currentTarget.value)}
              >
                <option value="" disabled>
                  בחירת חברה
                </option>
                {ctx.visibleCompanies.map((c) => (
                  // 09 §9.1 V1: the full legal name. Never a db code, never a ח.פ as the identity.
                  <option key={c.companyUid} value={c.companyUid}>
                    {c.legalName}
                  </option>
                ))}
              </select>
              {ctx.company === null ? null : <LifecycleChip status={ctx.company.lifecycleStatus} />}
            </label>
          </>
        ) : null}

        {ctx.module === null ? null : (
          <>
            <span className="context-bar__sep" aria-hidden="true">
              {CHEVRON}
            </span>
            <span className="context-bar__slot">
              <span className="context-bar__slot-label">המודול</span>
              <span className="context-bar__value">{ctx.module.displayNameHe}</span>
            </span>
          </>
        )}

        {ctx.entityLabelHe === null ? null : (
          <>
            <span className="context-bar__sep" aria-hidden="true">
              {CHEVRON}
            </span>
            <span className="context-bar__slot">
              <span className="context-bar__slot-label">הישות</span>
              {/* 09 §3.2: the middle truncates, never the current screen name. */}
              <span className="context-bar__value context-bar__value--current">{ctx.entityLabelHe}</span>
            </span>
          </>
        )}
      </nav>

      {/*
        The two controls the user reaches for constantly. They sit together and never move.
        09 §5.2: the period is ABSENT (not greyed) on a screen that is not period-bound.
      */}
      <div className="context-bar__controls">
        {ctx.periods.length === 0 ? null : (
          <label className="context-bar__slot">
            <span className="context-bar__slot-label">התקופה</span>
            <select
              className="context-bar__control"
              value={ctx.period?.periodUid ?? ''}
              onChange={(e) => selection.selectPeriod(e.currentTarget.value || null)}
            >
              <option value="">כל התקופות</option>
              {ctx.periods.map((p) => (
                <option key={p.periodUid} value={p.periodUid}>
                  {/* 09 §5.2: the resolved business form, never the raw source key. */}
                  {p.displayHe}
                </option>
              ))}
            </select>
            {ctx.period?.locked === true ? <span className="chip chip--lock">נעולה</span> : null}
          </label>
        )}

        {backToParent === undefined ? null : (
          <a className="context-bar__back" href={backToParent.href}>
            ← חזרה ל{backToParent.labelHe}
          </a>
        )}
      </div>

      {/* 09 §3.2: global utilities at the LEFT end — the RTL far corner. */}
      <div className="context-bar__utilities">
        <a className="context-bar__utility" href="#search">
          חיפוש
        </a>
        <a className="context-bar__utility" href="#documents">
          מסמכים
        </a>
        {/*
          CM-014: THE SURFACE SWITCH IS GONE, and its absence is the correction. It set a piece of
          React state, so a person on the tenant surface could put themselves on the platform
          surface by clicking. OA-0001 §2.1 withdrew the two-context identity: the surface comes
          from the session the server states, and there is nothing here that could change it.
        */}

        {/*
          SIGN OUT. **G-07's gap, open since Phase 1.**

          `auth.session.delete` was recorded CLOSED in the ledger on the strength of the route being
          declared and `SessionService.signOut` existing — and no control in this app posted to it.
          CM-023 struck that row; this is the control it was struck for.

          It is deliberately NOT "sign out everywhere": that is `user.session.revoke_all`, a
          different action with a different blast radius, and it lives on the personal-security
          screen where somebody choosing it can read what it does first.
        */}
        <button
          type="button"
          className="context-bar__utility"
          disabled={signingOut}
          onClick={() => void signOut()}
        >
          {signingOut ? 'מתנתק…' : 'יציאה'}
        </button>
      </div>
    </header>
  );
}
